import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Play, Pause, X, Heart, MoreHorizontal } from 'lucide-react';
import { useRoxboxPlayerContextSafe } from '@/contexts/RoxboxPlayerContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useColorExtraction } from '@/hooks/use-color-extraction';
import { useAuth } from '@/components/AuthProvider';
import { cn } from '@/lib/utils';
import { hapticLight, hapticMedium } from '@/lib/haptics';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TrackOptionsSheet } from './TrackOptionsSheet';

type GlobalMiniPlayerProps = {
  embeddedInV2Footer?: boolean;
};

export function GlobalMiniPlayer({ embeddedInV2Footer = false }: GlobalMiniPlayerProps) {
  const ctx = useRoxboxPlayerContextSafe();
  if (!ctx) return null;
  return <GlobalMiniPlayerInner embeddedInV2Footer={embeddedInV2Footer} ctx={ctx} />;
}

function GlobalMiniPlayerInner({ embeddedInV2Footer, ctx }: GlobalMiniPlayerProps & { ctx: NonNullable<ReturnType<typeof useRoxboxPlayerContextSafe>> }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [optionsOpen, setOptionsOpen] = useState(false);
  const isMobile = useIsMobile();

  const { 
    currentTrack, 
    currentAudiobookPage, 
    isPlaying, 
    togglePlay,
    currentTime, 
    duration,
    playingType,
    clearPlayer,
    setNowPlayingOpen,
    isNowPlayingOpen,
  } = ctx;

  // Check if track is liked
  const { data: isLiked = false } = useQuery({
    queryKey: ['liked-track', currentTrack?.id, user?.id],
    queryFn: async () => {
      if (!user || !currentTrack) return false;
      const { data } = await supabase
        .from('liked_tracks')
        .select('id')
        .eq('user_id', user.id)
        .eq('track_id', currentTrack.id)
        .maybeSingle();
      return !!data;
    },
    enabled: !!user && !!currentTrack && playingType === 'track',
  });

  // Toggle like mutation
  const toggleLike = useMutation({
    mutationFn: async () => {
      if (!user || !currentTrack) throw new Error('Not authenticated');
      
      if (isLiked) {
        await supabase
          .from('liked_tracks')
          .delete()
          .eq('user_id', user.id)
          .eq('track_id', currentTrack.id);
      } else {
        await supabase
          .from('liked_tracks')
          .insert({ user_id: user.id, track_id: currentTrack.id });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['liked-track', currentTrack?.id] });
      queryClient.invalidateQueries({ queryKey: ['liked-tracks'] });
      hapticMedium();
    },
  });

  // Dynamic color extraction
  const displayImage = playingType === 'audiobook' 
    ? currentAudiobookPage?.imageUrl 
    : currentTrack?.cover_image_url;
  const { colors } = useColorExtraction(displayImage);

  const hasContent = currentTrack || currentAudiobookPage;
  
  // Hide on Jukebox routes (DesktopPlayerBar/NowPlaying handle playback there)
  const isJukeboxRoute = location.pathname === '/jukebox' || 
                         location.pathname === '/roxbox' ||
                         location.pathname.startsWith('/jukebox/') || 
                         location.pathname.startsWith('/roxbox/');
  const isV2Route = location.pathname === '/';
  // V2Reader removed
  
  // Hide on home, my-bundles, profile, and settings pages
  const isHeaderMiniPlayerPage = location.pathname === '/' || 
                                  location.pathname === '/my-bundles' ||
                                  location.pathname === '/profile' ||
                                  location.pathname === '/settings';
  
  // Determine if player should be hidden
  const shouldHide = 
    isNowPlayingOpen ||
    isJukeboxRoute ||
    isHeaderMiniPlayerPage ||
    (isV2Route && !embeddedInV2Footer) ||
    location.pathname.startsWith('/letsbegin') ||
    location.pathname.startsWith('/userflow') ||
    location.pathname.startsWith('/read/') ||
    !hasContent;

  // Jello animation when NowPlaying closes
  const wasNowPlayingOpen = useRef(isNowPlayingOpen);
  const [jello, setJello] = useState(false);
  useEffect(() => {
    if (wasNowPlayingOpen.current && !isNowPlayingOpen && hasContent) {
      setJello(true);
      const t = setTimeout(() => setJello(false), 600);
      return () => clearTimeout(t);
    }
    wasNowPlayingOpen.current = isNowPlayingOpen;
  }, [isNowPlayingOpen, hasContent]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  
  const displayTitle = playingType === 'audiobook'
    ? `Page ${currentAudiobookPage?.pageNumber}`
    : currentTrack?.name;
  const displaySubtitle = playingType === 'audiobook'
    ? currentAudiobookPage?.bookTitle
    : currentTrack?.artist;

  const handleTitleClick = () => {
    hapticLight();
    if (!isV2Route) navigate('/');
    setNowPlayingOpen(true);
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    hapticLight();
    clearPlayer();
  };

  return (
    <div 
      className={cn(
        'transition-opacity duration-200',
        embeddedInV2Footer ? 'w-full' : 'fixed left-0 right-0 z-40 px-4',
        !embeddedInV2Footer && (isMobile ? '' : 'bottom-4 pb-2'),
        shouldHide && 'opacity-0 pointer-events-none'
      )}
      style={{
        ...(embeddedInV2Footer
          ? { pointerEvents: shouldHide ? 'none' : 'auto' }
          : isMobile
            ? {
                pointerEvents: shouldHide ? 'none' : 'none',
                bottom: 'calc(max(env(safe-area-inset-bottom), 8px) + 10px + 52px + 10px)',
              }
            : undefined),
        visibility: shouldHide ? 'hidden' : 'visible',
      }}
    >
      <div className={cn(embeddedInV2Footer && 'w-full')} style={{ pointerEvents: 'auto' }}>

        <div 
          className={cn("relative w-full rounded-full overflow-hidden", jello && 'animate-jello')}
          style={{
            background: 'rgba(255, 255, 255, 0.08)',
            boxShadow: '0 6px 6px rgba(0, 0, 0, 0.2), 0 0 20px rgba(0, 0, 0, 0.1)',
            border: '0.5px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          {/* Solid dark layer (no backdrop-blur – avoids per-frame resampling lag during swipes) */}
          <div
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              zIndex: 0,
              background: 'rgba(18, 18, 20, 0.82)',
            }}
          />
          {/* Tinted overlay using track dominant color */}
          <div
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              zIndex: 1,
              background: `linear-gradient(135deg, ${colors.dominant}40 0%, rgba(0,0,0,0.3) 100%)`,
            }}
          />
          {/* Progress bar at top - thinner */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-white/10 z-[2]">
            <motion.div
              className="h-full bg-white"
              style={{ width: `${progress}%` }}
              transition={{ duration: 0.1 }}
            />
          </div>

          <div className="flex items-center gap-2.5 pl-5 pr-2 py-1.5 relative z-[2]">
            {/* Track image */}
            <button
              onClick={handleTitleClick}
              className="relative w-7 h-7 shrink-0 rounded-[5px] overflow-hidden active:scale-95 transition-transform"
              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
            >
              <img
                src={displayImage}
                alt={displayTitle || 'Now playing'}
                className="w-full h-full object-cover"
              />
            </button>

            {/* Info */}
            <button 
              onClick={handleTitleClick}
              className="flex-1 min-w-0 text-left active:opacity-70 transition-opacity"
              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
            >
              <p className="text-white/90 text-[13px] font-bold truncate leading-tight">{displayTitle}</p>
              {displaySubtitle && (
                <p className="text-white/90 text-[11px] font-medium truncate leading-tight">{displaySubtitle}</p>
              )}
            </button>

            {/* Controls */}
            <div className="flex items-center gap-1" style={{ touchAction: 'manipulation' }}>
              {isV2Route ? (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      hapticMedium();
                      togglePlay();
                    }}
                    className="w-8 h-8 flex items-center justify-center shrink-0 active:scale-90 transition-transform text-white"
                    style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                  >
                    {isPlaying ? (
                      <Pause className="w-5 h-5" fill="currentColor" />
                    ) : (
                      <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
                    )}
                  </button>
                  <button
                    onClick={handleClose}
                    className="w-8 h-8 flex items-center justify-center shrink-0 active:scale-90 transition-transform text-white/70"
                    style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </>
              ) : (
                <>
                  {/* Like button - only for tracks */}
                  {playingType === 'track' && currentTrack && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (user) toggleLike.mutate();
                      }}
                      className="w-8 h-8 flex items-center justify-center shrink-0 active:scale-90 transition-transform"
                      style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                    >
                      <Heart 
                        className={cn("w-5 h-5", isLiked ? "text-primary fill-primary" : "text-white/50")} 
                      />
                    </button>
                  )}

                  {/* More options */}
                  {playingType === 'track' && currentTrack && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        hapticLight();
                        setOptionsOpen(true);
                      }}
                      className="w-8 h-8 flex items-center justify-center shrink-0 active:scale-90 transition-transform text-white/50"
                      style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                    >
                      <MoreHorizontal className="w-5 h-5" />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Track Options Sheet */}
      <TrackOptionsSheet 
        open={optionsOpen} 
        onOpenChange={setOptionsOpen} 
        track={currentTrack || null}
      />
    </div>
  );
}
